import { Component, ElementRef, inject, ViewChild } from '@angular/core';

import { RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { AppMenuitem } from './app.menuitem';
import { AuthService } from '@/services/auth.service';
import { TrpcService } from '@/services/trpc.service';
import { FeatureFlagService } from '@/services/feature-flag.service';
import { CommonService } from '@/services/common.service';

@Component({
    selector: '[app-menu]',
    standalone: true,
    imports: [AppMenuitem, RouterModule],
    template: `<div class="layout-menu-container" #menuContainer>
        <ul class="layout-menu">
            @for (item of model; track item; let i = $index) {

                @if (!item.separator) {
<li app-menuitem [item]="item" [index]="i" [root]="true"></li>
}
                @if (item.separator) {
<li class="menu-separator"></li>
}

}
        </ul>
    </div>`
})
export class AppMenu {
    el: ElementRef = inject(ElementRef);

    @ViewChild('menuContainer') menuContainer!: ElementRef;
    model: MenuItem[] = [];

    adminMenuItems: MenuItem[] = [
        {
            label: 'Dashboard',
            icon: 'pi pi-home',
            routerLink: ['/admin/dashboard']
        }, {
            label: 'Account Requests',
            icon: 'pi pi-users',
            routerLink: ['/admin/account-requests']
        }, {
            label: 'User Login Management',
            icon: 'pi pi-key',
            routerLink: ['/admin/manage-logins']
        }, {
            label: 'Tenant Management',
            icon: 'pi pi-user',
            routerLink: ['/admin/manage-tenants']
        }, {
            label: 'Lease Management',
            icon: 'pi pi-book',
            routerLink: ['/admin/manage-leases']
        }, {
            label: 'Suite Management',
            icon: 'pi pi-address-book',
            routerLink: ['/admin/manage-suites']
        }, {
            label: 'Monthly Billing',
            icon: 'pi pi-dollar',
            routerLink: ['/admin/monthly-billing']
        }, {
            label: 'View Invoices',
            icon: 'pi pi-receipt',
            routerLink: ['/admin/invoice-list']
        }, {
            label: 'Invoice Tenant',
            icon: 'pi pi-file-edit',
            routerLink: ['/admin/invoice-tenant']
        }, {
            label: 'Charge Code Management',
            icon: 'pi pi-tags',
            routerLink: ['/admin/charge-codes']
        }, {
            label: 'Receive Payment',
            icon: 'pi pi-wallet',
            routerLink: ['/admin/receive-payment']
        }, {
            label: 'Property Settings',
            icon: 'pi pi-sliders-h',
            routerLink: ['/admin/invoice-list']
        }, {
            label: 'Payment Transactions',
            icon: 'pi pi-file-excel',
            routerLink: ['/admin/payment-logs']
        }, {
            label: '',
            icon: 'pi pi-cog',
            items: [
                {
                    label: 'Global Settings',
                    icon: 'pi pi-sliders-h',
                    routerLink: ['/admin/settings']
                },
                {
                    label: 'System Status',
                    icon: 'pi pi-heart',
                    routerLink: ['/admin/system-status']
                },
                {
                    label: 'Email Logs',
                    icon: 'pi pi-envelope',
                    routerLink: ['/admin/email-logs']
                }
            ]
        }, {
            label: 'Link Tenant To ACH Deposit Account',
            icon: 'pi pi-link',
            routerLink: ['/admin/link-tenant-to-ach-deposit-account']
        }, {
            label: 'ACH Deposit Reconciliation',
            icon: 'pi pi-dollar',
            routerLink: ['/admin/ach-deposit-reconciliation']
        }
    ];

    tenantMenuItems: MenuItem[] = [
        {
            label: 'Dashboard',
            icon: 'pi pi-home',
            routerLink: ['/tenant/dashboard']
        }, {
            label: 'View Invoices',
            icon: 'pi pi-receipt',
            routerLink: ['tenant/invoice-list']
        }, {
            label: 'Leases',
            icon: 'pi pi-file-pdf',
            routerLink: ['/tenant/lease-documents']
        }
    ];

    accountingMenuItems: MenuItem[] = [
        {
            label: 'Dashboard',
            icon: 'pi pi-home',
            routerLink: ['/accounting/dashboard']
        },
        {
            label: 'View Invoices',
            icon: 'pi pi-receipt',
            routerLink: ['/accounting/invoice-list']
        },
        {
            label: 'Post Charges',
            icon: 'pi pi-money-bill',
            routerLink: ['/accounting/dashboard']
        },
        {
            label: 'Accounting Activity',
            icon: 'pi pi-save',
            routerLink: ['/accounting/accounting-activity']
        },
        {
            label: 'Generate Report',
            icon: 'pi pi-clipboard',
            routerLink: ['/accounting/dashboard']
        },
        {
            label: 'Invoice Tenant',
            icon: 'pi pi-file-edit',
            routerLink: ['/accounting/invoice-tenant']
        },
        {
            label: 'Charge Code Management',
            icon: 'pi pi-tags',
            routerLink: ['/accounting/charge-codes']
        },
        {
            label: 'Receive Payment',
            icon: 'pi pi-wallet',
            routerLink: ['/accounting/receive-payment']
        },
        {
            label: 'ACH Deposit Reconciliation',
            icon: 'pi pi-dollar',
            routerLink: ['/accounting/ach-deposit-reconciliation']
        },{
            label: 'Email Logs',
            icon: 'pi pi-envelope',
            routerLink: ['/accounting/email-logs']
        }
    ];
    constructor(
        private authService: AuthService,
        private trpcService: TrpcService,
        private featureFlagService: FeatureFlagService,
        private commonService: CommonService
    ) {


    }

    async ngOnInit() {

        let currentUser = this.authService.getCurrentUser();

        // Filter menu items based on feature flags
        const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
            if (this.featureFlagService.isAchEnabled()) {
                return items; // Show all items if ACH is enabled
            }

            // Filter out ACH-related menu items
            return items.filter(item => {
                const routerLink = item.routerLink?.[0];
                return routerLink !== '/admin/link-tenant-to-ach-deposit-account' &&
                       routerLink !== '/admin/ach-deposit-reconciliation' &&
                       routerLink !== '/accounting/ach-deposit-reconciliation';
            });
        };

        if (currentUser?.role == "admin") {
            this.model = filterMenuItems(this.adminMenuItems);
        } else if (currentUser?.role == "tenant") {
            this.model = this.tenantMenuItems;
            await this.loadSettings();
        } else if (currentUser?.role == "accounting") {
            this.model = filterMenuItems(this.accountingMenuItems);
        }

        this.model.push({
            label: 'Logout',
            icon: 'pi pi-sign-out',
            routerLink: ['/logout']
        });
    }
    async loadSettings() {
        try {
            const globalSettingResult = await this.trpcService.admin.getGlobalSettings.query();

            const isPaymentEnabled = this.commonService.toBooleanSafe(globalSettingResult?.['payment_enabled']);
            if(isPaymentEnabled) {
                this.model.push({
                    label: 'Payment Method Management',
                    icon: 'pi pi-credit-card',
                    routerLink: ['/tenant/manage-payment-methods']
                });
            }

        } catch (error: any) {
            console.error('Failed to load settings');
        }
    }
}
